import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import toast from 'react-hot-toast';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Stack,
  Chip,
  Paper,
  useMediaQuery,
} from "@mui/material";
import { Troubleshoot } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { EXCLUDED_FLIGHT_STATUS } from 'state/constants';
import { removeItemByValueImmutable } from 'state/helpers';
import DataGridComponent from "components/DataGridComponent";
import { ANOMALY_EPOCHS } from "../useAnomalyModel";
import MLTrainingSection from "./MLTrainingSection";


function getAnomalyLevel(pct) {
  if (pct >= 200) return { label: "Anomalous",  color: "error"   };
  if (pct >= 100) return { label: "Suspicious", color: "warning" };
  return               { label: "Normal",     color: "success" };
}

const columns = [
  { field: "flightNo",   headerName: "FlightNo", flex: 1 },
  {
    field: "flightDate", headerName: "Date", flex: 1.3,
    valueFormatter: (v) => v ? new Date(v).toLocaleDateString("en-GB") : "",
  },
  { field: "sector",     headerName: "Sector",  flex: 1.5, align: "center", headerAlign: "center" },
  { field: "acType",     headerName: "ACType", flex: 1 },
  { field: "flightReg",  headerName: "FlightReg", flex: 1 },
  { field: "std",        headerName: "Std", flex: 1 },
  { field: "sta",        headerName: "Sta", flex: 1 },
  {
    field: "anomalyPct", headerName: "Anomaly Score", flex: 3,
    renderCell: (params) => {
      const pct   = params.value ?? 0;
      const level = getAnomalyLevel(pct);
      return (
        <Stack direction="row" spacing={1} alignItems="center" height="100%" width="100%">
          <LinearProgress
            variant="determinate"
            value={Math.min(pct, 300) / 3}
            color={level.color}
            sx={{ flex: 1, height: 6, borderRadius: 3 }}
          />
          <Typography variant="caption" sx={{ minWidth: 42, textAlign: "right" }}>
            {pct}%
          </Typography>
        </Stack>
      );
    },
  },
  {
    field: "anomalyLevel", headerName: "Level", flex: 1.2,
    renderCell: (params) => {
      const level = getAnomalyLevel(params.row.anomalyPct ?? 0);

      return <Chip size="small" label={level.label} color={level.color} sx={{ fontWeight: 600 }} />;
    },
  },
];

function AnomalyCardItem({ row, theme }) {
  const level = getAnomalyLevel(row.anomalyPct ?? 0);
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        border: `1px solid ${theme.palette[level.color].main}`,
        borderRadius: 2,
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label={row.flightNo || "-"} />
            <Typography variant="body2" fontWeight={500}>
              {row.sector || `${row.dep ?? ""} → ${row.arr ?? ""}`}
            </Typography>
          </Stack>
          <Chip size="small" label={level.label} color={level.color} sx={{ fontWeight: 600 }} />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <LinearProgress
            variant="determinate"
            value={Math.min(row.anomalyPct ?? 0, 300) / 3}
            color={level.color}
            sx={{ flex: 1, height: 8, borderRadius: 4 }}
          />
          <Typography variant="caption" sx={{ minWidth: 42, textAlign: "right" }}>
            {row.anomalyPct}%
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2}>
          {row.flightDate && (
            <Typography variant="caption" color="text.secondary">
              {new Date(row.flightDate).toLocaleDateString("en-GB")}
            </Typography>
          )}
          {row.acType && (
            <Typography variant="caption" color="text.secondary">{row.acType}</Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {row.std || "-"} – {row.sta || "-"}
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
}

const AnomalyPanel = ({
  isModelReady, 
  isTraining, 
  trainingLogs, 
  threshold, 
  onTrain, 
  onReset, 
  predictAnomaly, 
  hideTraining = false 
}) => {
  const theme     = useTheme();
  const isMobile  = useMediaQuery("(max-width: 600px)");
  const flightsData = useSelector((state) => state.flights);

  const [scoredRows, setScoredRows] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  const [flights, setFlights] = useState([]); 

  useEffect(() => {        
    const processFlights = () => {
      try {
        const flights = removeItemByValueImmutable(flightsData, 'flightStatus', EXCLUDED_FLIGHT_STATUS);
        setFlights(flights);
      } catch (error) {
        toast.error(error?.message || "Failed to process flights!");
      } 
    };

    processFlights();        
  }, [flightsData]);
    
  const handleAnalyze = () => {
    if (!flights.length || !threshold) return;

    const scores = predictAnomaly(flights);
    if (!scores) return;

    const rows = flights.map((f, i) => {
      const pct = Math.round((scores[i] / threshold) * 100);
      return {
        ...f,
        anomalyScore: scores[i],
        anomalyPct:   pct,
        anomalyLevel: pct >= 200 ? "Anomalous" : pct >= 100 ? "Suspicious" : "Normal",
      };
    }).sort((a, b) => b.anomalyScore - a.anomalyScore);

    setScoredRows(rows);
  };

  const counts = useMemo(() => {
    if (!scoredRows) return null;
    return {
      anomalous:  scoredRows.filter((r) => r.anomalyPct >= 200).length,
      suspicious: scoredRows.filter((r) => r.anomalyPct >= 100 && r.anomalyPct < 200).length,
      normal:     scoredRows.filter((r) => r.anomalyPct < 100).length,
    };
  }, [scoredRows]);

  const thresholdChip = threshold !== null ? (
    <Chip size="small" label={`Threshold: ${(threshold * 1000).toFixed(3)}‰ MSE`} color="info" variant="outlined" />
  ) : null;

  return (
    <Box height="100%" display="flex" flexDirection="column" gap={2}>
      {!hideTraining && (
        <MLTrainingSection
          isModelReady={isModelReady}
          isTraining={isTraining}
          trainingLogs={trainingLogs}
          epochs={ANOMALY_EPOCHS}
          onTrain={onTrain}
          onReset={() => { onReset(); setScoredRows(null); }}
          leftLegend="Reconstruction Loss (MSE)"
          description="Train the autoencoder on historical flight data. It learns normal operation patterns and flags flights that deviate significantly - unusual routes, times, or aircraft type combinations. Anomaly threshold is set at mean + 2σ of training errors."
          readyExtra={thresholdChip}
        />
      )}

      {/* Analyze section - expands to fill remaining height */}
      {isModelReady && !isTraining && (
        <Box
          component="fieldset"
          sx={{
            flex: 1,
            minHeight: 0,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            component="legend"
            variant="subtitle1"
            sx={{ px: 0.5 }}
          >
            Analyze Loaded Data
          </Typography>

          <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
            >
              <Button
                variant="outlined"
                startIcon={<Troubleshoot />}
                onClick={handleAnalyze}
                disabled={!flights.length}
                sx={{ whiteSpace: "nowrap" }}
              >
                Analyze {flights.length} Flights
              </Button>
              {counts && (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip variant="outlined" size="small" label={`${counts.anomalous} Anomalous`}  color="error"   />
                  <Chip variant="outlined" size="small" label={`${counts.suspicious} Suspicious`} color="warning" />
                  <Chip variant="outlined" size="small" label={`${counts.normal} Normal`}         color="success" />
                </Stack>
              )}
            </Stack>

            {scoredRows && (
              isMobile ? (
                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
                  <Stack spacing={1}>
                    {scoredRows.map((row) => (
                      <AnomalyCardItem key={row._id} row={row} theme={theme} />
                    ))}
                  </Stack>
                </Box>
              ) : (
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <DataGridComponent
                    rows={scoredRows}
                    columns={columns}
                    columnVisibilityModel={columnVisibilityModel}
                    setColumnVisibilityModel={setColumnVisibilityModel}
                    props={{
                      density: "compact",
                      initialState: {
                        sorting: { sortModel: [{ field: "anomalyPct", sort: "desc" }] },
                      },
                    }}
                  />
                </Box>
              )
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default AnomalyPanel;
